import { zodResolver } from '@hookform/resolvers/zod';

import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { Controller,useForm } from 'react-hook-form';

import { ActionItemStatus, assignMotorcycleEngineNumberFormSchema } from '@ekhodealer/ekho-common';
import { Button, SkeletonRect,Stack, Text, TextInput, toast } from '@ekhodealer/ekho-common/rev';

import type { ActionItem } from '@ekhodealer/ekho-common';

import { useAssignMotorcycleEngineNumber } from '../../../api/progress/useAssignMotorcycleEngineNumber';
import { useGetAssignMotorcycleEngineNumber } from '../../../api/progress/useGetAssignMotorcycleEngineNumber';
import { navigateBackAtom, orderDetailsDrawerAtom } from '../../../atoms/orderDetailsDrawerAtoms';

interface AssignMotorcycleEngineNumberDetailProps {
	actionItem: ActionItem;
}

interface FormData {
	motorcycleEngineNumber: string;
}

/**
 * AssignMotorcycleEngineNumberDetail form component for assigning a motorcycle engine number to a purchased vehicle.
 * Displays a text input for engine number entry with validation and a submit button.
 */
export const AssignMotorcycleEngineNumberDetail = ({
	actionItem,
}: AssignMotorcycleEngineNumberDetailProps) => {
	const { selectedRow } = useAtomValue(orderDetailsDrawerAtom);
	const orderId = selectedRow?.purchasedVehicleId;
	const navigateBack = useSetAtom(navigateBackAtom);

	const assignMotorcycleEngineNumberMutation = useAssignMotorcycleEngineNumber(orderId);
	const { data: existingEngineNumberData, isLoading: isLoadingEngineNumberData } =
		useGetAssignMotorcycleEngineNumber(orderId);

	const {
		control,
		handleSubmit,
		formState: { errors, isSubmitting },
		reset,
	} = useForm<FormData>({
		resolver: zodResolver(assignMotorcycleEngineNumberFormSchema),
		defaultValues: {
			motorcycleEngineNumber: '',
		},
	});

	// Pre-populate form with existing motorcycle engine number data
	useEffect(() => {
		if (existingEngineNumberData?.motorcycleEngineNumber) {
			reset({
				motorcycleEngineNumber: existingEngineNumberData.motorcycleEngineNumber,
			});
		}
	}, [existingEngineNumberData, reset]);

	const submitHandler = handleSubmit(async (data: FormData) => {
		try {
			await assignMotorcycleEngineNumberMutation.mutateAsync({
				motorcycleEngineNumber: data.motorcycleEngineNumber.trim(),
			});
			toast({
				message: 'Motorcycle engine number assigned successfully',
				variant: 'success',
			});
			// Navigate back to progress view on success
			navigateBack();
			// Reset form on success
			reset();
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : 'Failed to assign motorcycle engine number';
			toast({
				message: errorMessage,
				variant: 'error',
			});
		}
	});

	return (
		<Stack itemsSpacing='24'>
			<Stack itemsSpacing='8'>
				<Text size='body' weight='semibold'>
					{actionItem.config.title}
				</Text>
				<Text size='bodySmall' color='secondary'>
					{actionItem.config.instructions ||
						'Enter the motorcycle engine number to assign to this order. The engine number length requirements may vary based on the services provider.'}
				</Text>
			</Stack>

			<Stack itemsSpacing='16'>
				{isLoadingEngineNumberData ? (
					<SkeletonRect width='100%' height={48} />
				) : (
					<Controller
						name='motorcycleEngineNumber'
						control={control}
						render={({ field: { onChange, onBlur, value } }) => (
							<Stack itemsSpacing='4'>
								<Text size='footnote'>Motorcycle Engine Number</Text>
								<TextInput
									placeholder='Enter motorcycle engine number'
									value={value}
									onChange={(val) => onChange(val.toUpperCase())}
									onBlur={onBlur}
									size='large'
									hasError={errors.motorcycleEngineNumber?.message}
									isDisabled={
										isSubmitting ||
										assignMotorcycleEngineNumberMutation.isPending ||
										actionItem.status === ActionItemStatus.COMPLETE
									}
								/>
								{errors.motorcycleEngineNumber && (
									<Text size='footnote' color='error'>
										{errors.motorcycleEngineNumber.message}
									</Text>
								)}
							</Stack>
						)}
					/>
				)}

				{actionItem.status !== ActionItemStatus.COMPLETE && (
					<Button
						size='large'
						isFullWidth
						onPress={() => {
							submitHandler();
						}}
						isLoading={isSubmitting || assignMotorcycleEngineNumberMutation.isPending}
						isDisabled={isLoadingEngineNumberData}>
						{actionItem.config.buttonTitle || 'Assign Motorcycle Engine Number'}
					</Button>
				)}
			</Stack>
		</Stack>
	);
};
